import { Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { LAMPORTS_PER_SOL, StakeProgram } from '@solana/web3.js'
import { TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useFetchUserWallet } from '../api'
import { StakeForm } from './stake/StakeForm'
import { StakeTabType, StakeTabs } from './StakeTabs'
import { StakingBenefits } from './StakingBenefits'
import { StakeAccount, UnstakeForm } from './unstake/UnstakeForm'
import { ValidatorInfo } from './ValidatorInfo'
import { WalletBlock } from './WalletBlock'

export const StakingInterface = () => {
  const { connection } = useConnection()
  const { publicKey } = useWallet()
  const { data: userWallet } = useFetchUserWallet()
  const [balance, setBalance] = useState<number>(0)
  const [stakeAccounts, setStakeAccounts] = useState<StakeAccount[]>([])
  const [isStakeAccountsLoading, setIsStakeAccountsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<StakeTabType>('stake')

  const fetchBalance = useCallback(async () => {
    if (!publicKey) return
    try {
      const bal = await connection.getBalance(publicKey)
      setBalance(bal / LAMPORTS_PER_SOL)
    } catch (error) {
      console.error('Failed to fetch balance:', error)
    }
  }, [connection, publicKey])

  const fetchStakeAccounts = useCallback(async () => {
    if (!publicKey) return
    setIsStakeAccountsLoading(true)
    try {
      // Get current epoch to determine if deactivation is complete
      const epochInfo = await connection.getEpochInfo()
      const currentEpoch = epochInfo.epoch

      const accounts = await connection.getParsedProgramAccounts(StakeProgram.programId, {
        filters: [
          {
            memcmp: {
              offset: 12,
              bytes: publicKey.toBase58()
            }
          }
        ]
      })

      type Delegation = { stake?: string; activationEpoch?: string; deactivationEpoch?: string }
      const parsedAccounts: StakeAccount[] = accounts.map((account) => {
        const parsed = account.account.data as {
          parsed?: { info?: { stake?: { delegation?: Delegation }; delegation?: Delegation } }
        }
        const info = parsed.parsed?.info
        const delegation = info?.stake?.delegation ?? info?.delegation
        const activatedLamports = delegation?.stake ? Number(delegation.stake) || 0 : 0
        let status: StakeAccount['status'] = 'inactive'

        if (delegation) {
          const activationEpoch = delegation.activationEpoch
          const deactivationEpoch = delegation.deactivationEpoch

          if (deactivationEpoch && deactivationEpoch !== '18446744073709551615') {
            // Deactivated in the same epoch as activated — no cooldown, withdraw immediately
            if (
              activationEpoch &&
              (activationEpoch === deactivationEpoch || currentEpoch > Number(deactivationEpoch))
            ) {
              status = 'inactive'
            } else if (activatedLamports === 0) {
              status = 'inactive' // Fully deactivated, ready to withdraw
            } else {
              status = 'deactivating' // Still cooling down
            }
          } else if (activationEpoch) {
            if (currentEpoch > Number(activationEpoch) && activatedLamports > 0) {
              status = 'active'
            } else {
              status = 'activating'
            }
          }
        }

        return {
          pubkey: account.pubkey,
          activationEpoch: delegation?.activationEpoch,
          deactivationEpoch: delegation?.deactivationEpoch,
          lamports: account.account.lamports,
          activatedLamports,
          status
        }
      })

      setStakeAccounts(parsedAccounts)
    } catch (error) {
      console.error('Failed to fetch stake accounts:', error)
    } finally {
      setIsStakeAccountsLoading(false)
    }
  }, [connection, publicKey])

  useEffect(() => {
    if (publicKey) {
      fetchBalance()
      fetchStakeAccounts()
    } else {
      setBalance(0)
      setStakeAccounts([])
    }
  }, [publicKey, fetchBalance, fetchStakeAccounts])

  const handleRefresh = useCallback(() => {
    fetchBalance()
    fetchStakeAccounts()
  }, [fetchBalance, fetchStakeAccounts])

  return (
    <>
      {userWallet?.wallet?.walletAddress &&
        publicKey &&
        publicKey?.toString() !== userWallet?.wallet?.walletAddress && (
          <div className="border-orange/30 mb-4 flex max-w-[600px] min-w-[300px] items-center gap-5 rounded-lg border border-solid px-5 py-3">
            <TriangleAlert className="text-orange shrink-0" />
            <Text type="body2-regular" className="text-gray-70 text-wrap">
              <Trans>
                The connected wallet doesn’t match the linked wallet. Please connect the correct wallet to continue.
              </Trans>
            </Text>
          </div>
        )}
      <div className="grid w-full max-w-[1200px] grid-cols-[475px_362px] grid-rows-[284px_1fr] gap-4 max-md:flex max-md:max-w-[500px] max-md:flex-col">
        <div className="row-span-2 flex h-full min-w-0 flex-col gap-2 max-md:order-2">
          <div className="relative flex grow flex-col gap-8 rounded-xl border border-gray-100 px-6 py-8 max-md:gap-6 max-md:p-5">
            <StakeTabs activeTab={activeTab} onTabChange={setActiveTab} />
            {activeTab === 'stake' ? (
              <StakeForm balance={balance} onSuccess={handleRefresh} />
            ) : (
              <UnstakeForm
                stakeAccounts={publicKey?.toString() === userWallet?.wallet?.walletAddress ? stakeAccounts : []}
                onSuccess={handleRefresh}
                isLoading={isStakeAccountsLoading}
              />
            )}
          </div>
        </div>
        <div className="max-mobile:w-full max-mobile:max-w-full col-start-2 row-start-1 h-[280px] max-w-[345px] max-md:order-1">
          <WalletBlock balance={balance} />
        </div>
        <div className="max-mobile:w-full max-mobile:max-w-full col-start-2 row-start-2 max-w-[345px] min-w-0 max-md:order-3 max-md:col-start-1">
          <ValidatorInfo />
        </div>
        <div className="max-mobile:mb-10 col-start-3 max-w-[345px] max-[1330px]:col-start-1 max-[1330px]:mt-4 max-[1330px]:ml-4 max-md:order-4">
          <StakingBenefits />
        </div>
      </div>
    </>
  )
}
